import { ActionContext, MutationTree, ActionTree, GetterTree } from 'vuex'
import webApi from '../webApi'
import { PreCarriageRoute, PreCarriageRouteRecords, PreCarriageFilter, Provider, PreCarriage, Vehicle } from '../webApi/DataModels'
import constants from '@/constants'
import { each, extend, find, orderBy } from 'lodash'
import moment from 'moment'

interface IPreCarriageListFilter {
  Provider_IDs: number[];
  From_Date: string;
  To_Date: string;
  Destination: string[];
}

function resolveValues (id: number, values: any[], idFieldName: string, valueFieldName: string, resolvedLabel: string): String {
  var resolvedValues = []
  var filterObject = {}
  filterObject[idFieldName] = id
  var value = find(values, filterObject)
  if (value && value[valueFieldName]) {
    resolvedValues.push(value[valueFieldName])
  }
  if (resolvedValues.length > 3) {
    return resolvedValues.length + ' ' + resolvedLabel
  } else if (resolvedValues.length === 0) {
    return ''
  } else {
    return resolvedValues.join(', ')
  }
}

function findCustomerName (id) {
  if (id === undefined) { return '' }
  const customers = JSON.parse(window.sessionStorage.getItem('Customer_List'))
  const idCustomer = id.split(',').map(id => parseInt(id))
  const nameArray = []

  customers.forEach(item => {
    if (idCustomer.includes(item.Customer_ID)) {
      nameArray.push(item.Customer_Name)
    }
  })

  return nameArray.join(', ')
}

function destinationFilter () {
  var precarriageList = JSON.parse(window.sessionStorage.getItem('PreCarriageRoute_List'))
  const distinct: any = Object.values(precarriageList.reduce((o, a) => { o[a.PreCarriage_Route_Destination] = a; return o }, {}))
  var resultArray = []

  resultArray = distinct.map((preCarriageRoute: any) => {
    return {
      value: preCarriageRoute.PreCarriage_Route_Destination,
      label: preCarriageRoute.PreCarriage_Route_Destination,
      selected: false
    }
  })
  return resultArray
}

class PreCarriageRouteState {
  public preCarriageList: PreCarriageRouteRecords[] = []
  public filteredPreCarriageList: PreCarriageRouteRecords[] = []
  public loadedPreCarriage: PreCarriage = null
  public preCarriageRoutes: PreCarriageRoute[] = []
  public preCarriageRouteOptions: any[] = []
  public preCarriageFilter: PreCarriageFilter = null
  public preCarriageResult: any = null
  public preCarriageDestination: any = []
}

const mutations = <MutationTree<PreCarriageRouteState>>{
  setPreCarriageList (state: PreCarriageRouteState, { list, providers, vehicles }: { list: PreCarriageRouteRecords[], providers: Provider[], vehicles: Vehicle[] }) {
    each(list, (preCarriageItem: any, index: number) => {
      extend(preCarriageItem, {
        PreCarriage_Route_Records_Provider_Name: resolveValues(preCarriageItem.PreCarriage_Route_Records_Provider_ID, providers, 'Provider_ID', 'Provider_Name', 'Providers')
      })

      extend(preCarriageItem, {
        PreCarriage_Route_Records_Vechicle_Name: resolveValues(preCarriageItem.PreCarriage_Route_Records_Vechicle_ID, vehicles, 'Vehicle_ID', 'Vehicle_Type', 'Vehicles')
      })

      extend(preCarriageItem, {
        PreCarriage_Route_Customer_Name: findCustomerName(preCarriageItem.PreCarriage_Route_Records_Customer_ID)
      })
    })

    state.preCarriageList = list
    state.filteredPreCarriageList = list
  },
  setLoadedPreCarriage (state: PreCarriageRouteState, preCarriage: PreCarriage): void {
    state.loadedPreCarriage = preCarriage
  },
  filteredPreCarriageList (state:PreCarriageRouteState, { sortFields, sortDirections }) {
    state.filteredPreCarriageList = orderBy(state.filteredPreCarriageList, sortFields, sortDirections)
    state.preCarriageList = orderBy(state.preCarriageList, sortFields, sortDirections)
  },
  setPreCarriageFilter (state: PreCarriageRouteState, preCarriageFilter: PreCarriageFilter) {
    state.preCarriageFilter = preCarriageFilter
  },
  setActivePreCarriageRoutes (state: PreCarriageRouteState, preCarriageRoutes: any[]) {
    each(preCarriageRoutes, (preCarriageItem: any, index: number) => {
      extend(preCarriageItem, {
        PreCarriage_Route_Records_Provider_Name: resolveValues(preCarriageItem.PreCarriage_Route_Records_Provider_ID, this.state.provider.providers, 'Provider_ID', 'Provider_Name', 'Providers')
      })

      extend(preCarriageItem, {
        PreCarriage_Route_Records_Vechicle_Name: resolveValues(preCarriageItem.PreCarriage_Route_Records_Vechicle_ID, this.state.vehicle.vehicles, 'Vehicle_ID', 'Vehicle_Type', 'Vehicles')
      })

      extend(preCarriageItem, {
        PreCarriage_Route_Customer_Name: findCustomerName(preCarriageItem.PreCarriage_Route_Customer_ID)
      })
    })
    state.preCarriageRoutes = preCarriageRoutes
    state.preCarriageRouteOptions = preCarriageRoutes.map((preCarriageRoute: any) => {
      return {
        value: preCarriageRoute.PreCarriage_Route_ID,
        label: preCarriageRoute.PreCarriage_Route_Description,
        selected: false
      }
    })
    window.sessionStorage.setItem('PreCarriageRoute_List', JSON.stringify(state.preCarriageRoutes))
    state.preCarriageDestination = destinationFilter()
  },
  reset (state:PreCarriageRouteState) {
    state.preCarriageRoutes = []
    state.preCarriageRouteOptions = []
    state.preCarriageList = []
    state.filteredPreCarriageList = []
    state.loadedPreCarriage = null
    state.preCarriageResult = null
    state.preCarriageFilter = null
    state.preCarriageDestination = []
  },
  resetPreCarriage (state: PreCarriageRouteState): void {
    state.loadedPreCarriage = null
  }
}

const actions = <ActionTree<PreCarriageRouteState, any>>{
  reset (store: ActionContext<PreCarriageRouteState, any>):void {
    store.commit('reset')
  },
  loadPreCarriageFilter (store: ActionContext<PreCarriageRouteState, any>, preCarriageFilter: PreCarriageFilter) {
    store.commit('setPreCarriageFilter', preCarriageFilter)
  },
  // loads the preCarriage list
  loadPreCarriageList (store: ActionContext<PreCarriageRouteState, any>, preCarriageListFilter: IPreCarriageListFilter): Promise<any> {
    return webApi.preCarriage_route.get_PreCarriage_List(preCarriageListFilter).then(
      (response) => {
        store.commit('setPreCarriageList', {
          list: response.data,
          providers: store.rootState.provider.activeProviders,
          vehicles: store.rootState.vehicle.vehicles
        })
        return Promise.resolve('success')
      }).catch((response) => {
      window.bus.$emit('openErrorModal', response.data.ExceptionMessage)
      return Promise.reject(response)
    })
  },
  // loads a preCarriage
  loadPreCarriage (store: ActionContext<PreCarriageRouteState, any>, preCarriageID: number): Promise<any> {
    store.commit('resetPreCarriage')
    return webApi.preCarriage_route.get_PreCarriage_By_ID(preCarriageID).then(
      (response: any) => {
        var result: PreCarriage = response.data as PreCarriage
        store.commit('setLoadedPreCarriage', result)
        return Promise.resolve('success')
      }).catch((response) => {
      window.bus.$emit('openErrorModal', response.data.ExceptionMessage)
      return Promise.reject(response)
    })
  },
  loadPreCarriageRoutes (store: ActionContext<PreCarriageRouteState, any>):Promise<any> {
    return store.dispatch('user/hasPermission', { permissionType: constants.PERMISSION_TYPES.PROVIDER, permissionValue: constants.PERMISSION_VALUES.LIST }, { root: true })
      .then((hasPermission): any => {
        if (!hasPermission) {
          return Promise.resolve('not needed')
        } else {
          if (window.sessionStorage.getItem('PreCarriageRoute_List') != null) {
          // the list has already been stored in session
            store.commit('setActivePreCarriageRoutes', JSON.parse(window.sessionStorage.getItem('PreCarriageRoute_List')))
            return Promise.resolve('success')
          } else {
          // the list has not been stored in session yet, load from server
            return webApi.preCarriage_route.get_PreCarriage_Routes().then(
              (response) => {
                // Store the response
                store.commit('setActivePreCarriageRoutes', response.data)
                return Promise.resolve('success')
              }).catch((response) => {
              window.bus.$emit('openErrorModal', 'error loading preCarriageRoutes: ' + response.ExceptionMessage)
              return Promise.reject(response)
            })
          }
        }
      })
  },
  savePreCarriageList (store: ActionContext<PreCarriageRouteState, any>, preCarriageRoute: any): Promise<any> {
    return webApi.preCarriage_route.save_PreCarriage_Routes(preCarriageRoute).then(
      (response) => {
        var filter = {
          providers: [],
          fromDate: moment().format(),
          toDate: moment().format(),
          destination: []
        }
        store.commit('setPreCarriageFilter', filter)
        return Promise.resolve(response)
      }).catch((response) => {
      window.bus.$emit('openErrorModal', response.data.ExceptionMessage)
      return Promise.reject(response)
    })
  },
  sortList ({ commit }, { sortFields, sortDirections }): void {
    commit('filteredPreCarriageList', { sortFields, sortDirections })
  }
}

const getters = <GetterTree<PreCarriageRouteState, any>>{
  preCarriageRoutes: (state) => {
    return state.preCarriageRoutes
  },
  preCarriageRouteOptions: (state) => {
    return state.preCarriageRouteOptions
  },
  preCarriageList: (state): PreCarriageRouteRecords[] => {
    return state.filteredPreCarriageList
  },
  loadedPreCarriage: (state): PreCarriage => {
    return state.loadedPreCarriage
  },
  preCarriageResult: (state): any => {
    return state.preCarriageResult
  },
  preCarriageFilter: (state: PreCarriageRouteState): PreCarriageFilter => {
    return state.preCarriageFilter
  },
  preCarriageDestination: (state: PreCarriageRouteState): any => {
    return state.preCarriageDestination
  }
}
export default ({
  namespaced: true,
  state: new PreCarriageRouteState(),
  mutations,
  actions,
  getters
})
